// hooks that determine which server (Midgard or Askr) to fetch data from

import { useIsMidgardDDAEnabled } from "components/Dashboard/GroupMetrics/useMetrics";
import { getIsCypressRunning } from "constants/constants";
import {
  getQuery,
  useIsTrendChartExpandedHubletPage,
  useIsTrendChartExpandedTimelinePage,
  useIsTrendChartExpandedTrendsPage,
} from "pages/DashboardPages/dashboardPagesUtils";
import { GRID_LAYOUT_COMPONENTS } from "pages/GridLayoutPage/defaultGridLayoutComponents";
import {
  useStorybookQuery,
  GridLayoutComponentsObj,
  getGridLayoutComponents,
} from "pages/GridLayoutPage/gridLayoutPageUtils";
import { useLocation } from "react-router";
import { useSelector } from "store/useSelector";
import { BreakpointKeysEnum, TrendChartBinSizesEnum } from "types/enums";
import {
  useDisplayTrendsPageTimeline,
  useFocusRange,
  useGetPageFromPath,
  useIsSingleProcessPage,
  useTrendsPageFilterState,
  useVisibleGroups,
} from "./genericHooks";
import { useIsStorybook } from "./storyHooks";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { ONE_DAY_OF_MS } from "utils/timeUtils";
import { useCustomerTimeZoneOffsetMs } from "utils/timeZoneHooks";
import { useDisplayTimelinePageTimelines } from "pages/DashboardPages/DashboardPagesContent/useDisplayTimelinePageTimelines";
import { useMemo } from "react";

/** use DDAs for Group Metrics if we need metrics that are only available from DDA e.g. OPEQ, USLE, count, etc */
export function useShouldUseDataDumpsForGroupMetrics() {
  const isFromSnapshotsPageTimelineOnly = useIsFromSnapshotsPageTimelineOnly();
  return useIsMidgardDDAEnabled() && !isFromSnapshotsPageTimelineOnly;
}

// Trend chart DDAs: some customers (e.g. gutterglove) show trend chart counts (Midgard DDAs),
// but still source their bars from Askr aggregates.
// So, we have separate should-use-DDAs hooks for 1. Barstacks and 2. Counts

/** TrendChart bars: uses dataDumpsTrendChart if... */
export function useShouldUseDataDumpsForTrendChartBarstacks() {
  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();
  const shouldEnableTrendChartDDAs = useShouldEnableTrendChartDDAs();
  const [
    {
      general: { hideProducingLosses },
    },
  ] = useFlightConfig();

  return useMemo(
    () =>
      shouldEnableTrendChartDDAs &&
      // ...the customer uses the PnL Losses version of the PnL chart (need these data dumps for the PnL Popup)
      isMidgardDDAEnabled &&
      !hideProducingLosses,
    [shouldEnableTrendChartDDAs, isMidgardDDAEnabled, hideProducingLosses]
  );
}

/** TrendChartCenterMetric: uses dataDumpsTrendChart if... */
export function useShouldUseDataDumpsForTrendChartCenterMetric() {
  const shouldEnableTrendChartDDAs = useShouldEnableTrendChartDDAs();
  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();

  return useMemo(
    () =>
      shouldEnableTrendChartDDAs &&
      // ...the customer uses data dumps for trend chart bar center metric
      isMidgardDDAEnabled,
    [shouldEnableTrendChartDDAs, isMidgardDDAEnabled]
  );
}

/** TrendChart counts: uses dataDumpsTrendChart (and dataDumpSinceLotStart if tracking lot progress)  if...*/
export function useShouldUseDataDumpsForTrendChartCounts() {
  const [flightConfig] = useFlightConfig();
  const showCounts = flightConfig.trendChart.showCounts;
  const shouldEnableTrendChartDDAs = useShouldEnableTrendChartDDAs();

  return useMemo(
    () =>
      shouldEnableTrendChartDDAs &&
      // ...the customer uses data dumps for trend chart bar counts
      showCounts,
    [shouldEnableTrendChartDDAs, showCounts]
  );
}

/** we need to fetch the trend chart DDAs for either counts, bars, or center metric */
export function useShouldFetchTrendChartDataDumps() {
  const shouldUseDataDumpsForTrendChartCounts =
    useShouldUseDataDumpsForTrendChartCounts();
  const shouldUseDataDumpsForTrendChartBarstacks =
    useShouldUseDataDumpsForTrendChartBarstacks();
  const shouldUseDataDumpsForTrendChartCenterMetric =
    useShouldUseDataDumpsForTrendChartCenterMetric();
  const {
    isOperatorPage,
    isTier0Page,
    isTimelinePage,
    isTrendsPage,
    isHubletPage,
  } = useGetPageFromPath();
  const timezoneOffsetMs = useCustomerTimeZoneOffsetMs();

  const focusRange = useFocusRange();

  const [isTrendChartExpandedTimelinePage] =
    useIsTrendChartExpandedTimelinePage();
  const [isTrendChartExpandedTrendsPage] = useIsTrendChartExpandedTrendsPage();
  const [isTrendChartExpandedHubletPage] = useIsTrendChartExpandedHubletPage();

  return useMemo(() => {
    const ready = focusRange && (timezoneOffsetMs || timezoneOffsetMs === 0);
    // on trends page, fetch only if trend chart is expanded
    if (isTrendsPage && !isTrendChartExpandedTrendsPage) {
      return false;
    }

    if (isHubletPage && !isTrendChartExpandedHubletPage) {
      return false;
    }

    // on timeline page, fetch only if trend chart is expanded
    if (
      (isTimelinePage && !isTrendChartExpandedTimelinePage) ||
      window.location.search.toLowerCase().includes("hidetrend=true")
    ) {
      return false;
    }

    const shouldFetch =
      ready &&
      // ...the customer uses data dumps for trend chart bar counts
      (shouldUseDataDumpsForTrendChartCounts ||
        // ...or uses data dumps for trend chart bar stacks
        shouldUseDataDumpsForTrendChartBarstacks ||
        // ...or uses data dumps for trend chart bar center metric
        shouldUseDataDumpsForTrendChartCenterMetric ||
        // ...or Operator Page (which uses these DDAs)
        isOperatorPage ||
        // ...or Tier0 page (which uses these DDAs)
        isTier0Page ||
        // ...or if we're in a cypress test
        getIsCypressRunning());
    return shouldFetch;
  }, [
    isTrendsPage,
    isTrendChartExpandedTrendsPage,
    isTimelinePage,
    isTrendChartExpandedTimelinePage,
    shouldUseDataDumpsForTrendChartCounts,
    shouldUseDataDumpsForTrendChartBarstacks,
    shouldUseDataDumpsForTrendChartCenterMetric,
    isOperatorPage,
    isTier0Page,
    focusRange,
    timezoneOffsetMs,
    isHubletPage,
    isTrendChartExpandedHubletPage,
  ]);
}

/** flightConfig.trendChart.overrideCountMachemetryIdsPerGroup allows us to specify a custom set of machemetryIds per-process-group to display on the trend chart */
export function useShouldFetchCustomTrendChartDataDumpsForCount() {
  const shouldUseDataDumpsForTrendChartCounts =
    useShouldUseDataDumpsForTrendChartCounts();
  const shouldUseDataDumpsForTrendChartBarstacks =
    useShouldUseDataDumpsForTrendChartBarstacks();
  const shouldUseDataDumpsForTrendChartCenterMetric =
    useShouldUseDataDumpsForTrendChartCenterMetric();
  const { isOperatorPage } = useGetPageFromPath();
  const timezoneOffsetMs = useCustomerTimeZoneOffsetMs();
  const isSingleProcessPage = useIsSingleProcessPage();
  const [
    {
      trendChart: { overrideCountMachemetryIdsPerGroup },
    },
  ] = useFlightConfig();
  const visibleGroups = useVisibleGroups();
  const focusRange = useFocusRange();
  return useMemo(() => {
    const visibleGroup = visibleGroups.length === 1 ? visibleGroups[0] : null;
    const validOverride =
      visibleGroup &&
      (overrideCountMachemetryIdsPerGroup?.[visibleGroup.longName] ||
        overrideCountMachemetryIdsPerGroup?.[visibleGroup.shortName]);

    const ready = focusRange && (timezoneOffsetMs || timezoneOffsetMs === 0);
    const shouldFetch =
      ready &&
      !isSingleProcessPage &&
      validOverride &&
      // ...the customer uses data dumps for trend chart bar counts
      (shouldUseDataDumpsForTrendChartCounts ||
        // ...or uses data dumps for trend chart bar stacks
        shouldUseDataDumpsForTrendChartBarstacks ||
        // ...or uses data dumps for trend chart bar center metric
        shouldUseDataDumpsForTrendChartCenterMetric ||
        // ...or Operator Page (which uses these DDAs)
        isOperatorPage ||
        // ...or if we're in a cypress test
        getIsCypressRunning());

    return shouldFetch;
  }, [
    visibleGroups,
    focusRange,
    timezoneOffsetMs,
    shouldUseDataDumpsForTrendChartCounts,
    shouldUseDataDumpsForTrendChartBarstacks,
    shouldUseDataDumpsForTrendChartCenterMetric,
    isOperatorPage,
    isSingleProcessPage,
    overrideCountMachemetryIdsPerGroup,
  ]);
}

/** PnlSummaryChart uses dataDumpsFullRangeByProcess */
export function useShouldUseDataDumpsForPnlChart() {
  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();
  const [
    {
      general: { overrideDdaLimitDays },
    },
  ] = useFlightConfig();
  const focusRange = useFocusRange();
  const { isTrendsPage } = useGetPageFromPath();
  return useMemo(() => {
    const focusRangeDuration = focusRange ? focusRange.lt - focusRange.ge : 0;
    const focusRangeDurationDays = focusRangeDuration / ONE_DAY_OF_MS;
    const isBelowDdaLimit =
      Boolean(overrideDdaLimitDays) &&
      focusRangeDurationDays <= overrideDdaLimitDays;

    return isMidgardDDAEnabled && (isTrendsPage ? isBelowDdaLimit : true);
  }, [isMidgardDDAEnabled, isTrendsPage, focusRange, overrideDdaLimitDays]);
}

/** HubletView uses dataDumpHubletView, for counts/rates */
export function useShouldUseDataDumpForHubletView() {
  const { isHubletPage, isGridLayoutPage, isOperatorPage } =
    useGetPageFromPath();
  const isStorybook = useIsStorybook();
  const storybookQuery = useStorybookQuery();
  const location = useLocation();
  return useMemo(() => {
    const urlQuery = getQuery(location);
    const layoutComponentsObj: GridLayoutComponentsObj =
      getGridLayoutComponents(
        isStorybook ? storybookQuery : urlQuery,
        BreakpointKeysEnum.lg
      );
    const showOnGridPage =
      GRID_LAYOUT_COMPONENTS.hubletView in layoutComponentsObj;

    return (
      !isOperatorPage && (isHubletPage || (isGridLayoutPage && showOnGridPage))
    );
  }, [
    isHubletPage,
    isGridLayoutPage,
    isOperatorPage,
    isStorybook,
    storybookQuery,
    location,
  ]);
}

/** MachineBreakdownChart uses dataDumpsFullRangeByProcess */
export function useShouldUseDataDumpsForMachineBreakdownChart() {
  return useShouldUseDataDumpsForPnlChart();
}

/** Butterfly (OverallBarstack) uses dataDumpsFullRangeByProcess (single-process pages) or dataDumpFullRange (multi-process pages) */
export function useShouldUseDataDumpsForButterfly() {
  const [
    {
      general: { hideProducingLosses },
    },
  ] = useFlightConfig();
  return useShouldUseDataDumpsForGroupMetrics() && !hideProducingLosses;
}

/** ProcessTile uses dataDumpsFullRangeByProcess */
export function useShouldUseDataDumpsForProcessTile() {
  return useShouldUseDataDumpsForPnlChart();
}

/** TileButterflyModule uses dataDumpsFullRangeByProcess */
export function useShouldUseDataDumpsForTileButterfly() {
  return useShouldUseDataDumpsForButterfly();
}

/** LoadingIndicatorDashboardPages uses dataDumpsFullRangeByProcess (single-process pages) or dataDumpFullRange (multi-process pages) */
export function useShouldUseDataDumpsForLoadingIndicator() {
  const { isTrendsPage, isGroupTilesPage, isProcessTilesPage } =
    useGetPageFromPath();
  const { isTrendsPageFilteredByShift } = useTrendsPageFilterState();
  const isPageWithTrendChart = useIsPageWithTrendChart();
  const shouldUseDataDumpsForTrendChartBarstacks =
    useShouldUseDataDumpsForTrendChartBarstacks();
  const shouldUseDataDumpsForButterfly =
    useShouldUseDataDumpsForTileButterfly();
  const shouldUseDataDumpsForGroupMetrics =
    useShouldUseDataDumpsForGroupMetrics();
  return useMemo(() => {
    return isTrendsPage
      ? // filter-by-shift only works with Askr aggregates
        shouldUseDataDumpsForTrendChartBarstacks && !isTrendsPageFilteredByShift
      : isPageWithTrendChart
        ? shouldUseDataDumpsForTrendChartBarstacks
        : isGroupTilesPage || isProcessTilesPage
          ? shouldUseDataDumpsForGroupMetrics
          : shouldUseDataDumpsForButterfly;
  }, [
    isTrendsPage,
    isGroupTilesPage,
    isProcessTilesPage,
    isTrendsPageFilteredByShift,
    isPageWithTrendChart,
    shouldUseDataDumpsForTrendChartBarstacks,
    shouldUseDataDumpsForGroupMetrics,
    shouldUseDataDumpsForButterfly,
  ]);
}

function useShouldEnableTrendChartDDAs() {
  const isTrendChartVisible = useIsPageWithTrendChart();
  const isTrendChartBinSizeUnder24hrs = useIsTrendChartBinSizeUnder24hrs();
  const isTrendsPageFinishedInitialFilter = useSelector(
    state => state.legacy.isTrendsPageFinishedInitialFilter
  );
  const [
    {
      general: { accessToTrendsPage },
    },
  ] = useFlightConfig();
  const { isTrendsPage } = useGetPageFromPath();

  return useMemo(
    () =>
      (isTrendsPage
        ? (accessToTrendsPage || getIsCypressRunning()) &&
          isTrendsPageFinishedInitialFilter
        : true) &&
      isTrendChartVisible &&
      isTrendChartBinSizeUnder24hrs,
    [
      accessToTrendsPage,
      isTrendsPage,
      isTrendsPageFinishedInitialFilter,
      isTrendChartVisible,
      isTrendChartBinSizeUnder24hrs,
    ]
  );
}

function useIsPageWithTrendChart() {
  const { isHubletPage, isTrendsPage, isTimelinePage, isGridLayoutPage } =
    useGetPageFromPath();
  return useMemo(
    () => isHubletPage || isTrendsPage || isTimelinePage || isGridLayoutPage,
    [isHubletPage, isTrendsPage, isTimelinePage, isGridLayoutPage]
  );
}

function useIsTrendChartBinSizeUnder24hrs() {
  const trendChartBinSize = useSelector(
    state => state.legacy.trendChartBinSize
  );

  return useMemo(
    () =>
      [
        TrendChartBinSizesEnum.days,
        TrendChartBinSizesEnum.hours,
        TrendChartBinSizesEnum.shifts,
      ].includes(trendChartBinSize),
    [trendChartBinSize]
  );
}

export function useShouldFetchSegments() {
  const [showTimelines] = useDisplayTimelinePageTimelines();
  const [isDisplayTimeline] = useDisplayTrendsPageTimeline();
  const {
    isGroupTilesPage,
    isProcessTilesPage,
    isHubletPage,
    isGridLayoutPage,
    isTrendsPage,
  } = useGetPageFromPath();
  return useMemo(() => {
    if (isGroupTilesPage) {
      return false;
    }
    if (showTimelines || window.location.pathname.includes("/timeline/")) {
      return true;
    }
    const isTrendsPageTimelineVisible = isTrendsPage && isDisplayTimeline;
    const isTakingSnapshotTimelinePage =
      window.location.href.includes("fromSnapshots") &&
      window.location.href.includes("view=tl");
    if (
      isProcessTilesPage ||
      isHubletPage ||
      isGridLayoutPage ||
      isTrendsPageTimelineVisible ||
      isTakingSnapshotTimelinePage
    ) {
      return true;
    }
    return false;
  }, [
    showTimelines,
    isDisplayTimeline,
    isGroupTilesPage,
    isProcessTilesPage,
    isHubletPage,
    isGridLayoutPage,
    isTrendsPage,
  ]);
}

export function useIsFromSnapshotsPageTimelineOnly() {
  const { isFromSnapshotsPage } = useGetPageFromPath();
  return useMemo(() => {
    const search = window.location.search;
    const isFromSnapshotsPageTimelineOnly =
      isFromSnapshotsPage &&
      search.includes("hideTrend=true") &&
      search.includes("hidePnl=true") &&
      search.includes("hideSummary=true");
    return isFromSnapshotsPageTimelineOnly;
  }, [isFromSnapshotsPage]);
}
